// src/pages/Legal/AboutPage.tsx
import { Link } from 'react-router-dom';
import MainLayout from '../../components/MainLayout/MainLayout';
import useSEO from '../../hooks/useSEO';
import { 
  Globe, 
  Award,
  ArrowRight,
  Clapperboard,
  Headphones,
  Palette,
  Eye,
  Users,
  Shield,
  Mic,
  Film,
  Sparkles,
  Star,
  Info,
  Handshake,
  Megaphone,
  Briefcase,
  CheckCircle2
} from 'lucide-react';

/**
 * StrymHub — Independent production company and streaming home.
 * Based in Sheffield, UK. Builds an in-house repertory of actors, writers,
 * and crew scouted from local communities and universities. Original work
 * is produced under the StrymHub label and streamed exclusively here.
 */

const AboutPage = () => {
  // Apply SEO for about page with enhanced metadata
  useSEO({
    title: 'About StrymHub — Independent Production Company, Streaming Home & Partner-Friendly Studio',
    description: 'StrymHub is an independent production company based in Sheffield, UK. We produce original films, series and shows in-house, stream them exclusively on our own platform, and actively partner with brands, sponsors, collaborators and other businesses on co-produced and sponsored content.',
    keywords: 'strymhub, sheffield production company, independent film uk, in-house production, streaming platform uk, brand partnerships, sponsored content, branded content, co-production, sponsorship opportunities, sheffield filmmakers, uk independent studio',
    type: 'website'
  });

  // What StrymHub is, at a glance. Honest, not inflated.
  const stats = [
    { label: 'Based In', value: 'Sheffield', icon: Globe, description: 'Yorkshire, United Kingdom' },
    { label: 'Model', value: 'In-House', icon: Film, description: 'We produce, we distribute, we own' },
    { label: 'Talent Source', value: 'Community', icon: Users, description: 'Local actors, writers, crew, students' },
    { label: 'Stage', value: 'Early', icon: Sparkles, description: 'Building the foundation, releasing as we go' }
  ];

  const features = [
    {
      icon: Clapperboard,
      title: 'Original Films & Series',
      description: 'Every film, series, and short on StrymHub is produced in-house under our own label. We write it, cast it, shoot it, edit it, and release it here first. No licensing deals. No third-party catalogue. Just our work.',
      details: ['Written and produced by StrymHub', 'Cast from our in-house repertory', 'Released exclusively on this platform', 'You own a slice of the story by watching']
    },
    {
      icon: Users,
      title: 'An In-House Repertory',
      description: 'We scout actors, writers, directors, and crew from Sheffield, surrounding communities, and UK universities. Talent that joins StrymHub becomes part of an ongoing company — not a one-off casting call. Familiar faces. Recurring collaborators. A growing creative family.',
      details: ['Community-scouted talent', 'University collaborations', 'Recurring cast and crew', 'Credits, profit share, and a career path']
    },
    {
      icon: Palette,
      title: 'Stories Rooted In Place',
      description: 'Sheffield, Yorkshire, and the UK have stories the big platforms ignore. We make work that comes from where we live — but is made to travel. Local roots, global audience.',
      details: ['UK-based storytelling', 'Local locations and voices', 'Made for a global audience', 'Authentic, not algorithmic']
    },
    {
      icon: Eye,
      title: 'Behind Every Production',
      description: 'You won\u2019t just watch the finished work. We share the process — production diaries, rehearsal footage, interviews with the cast and crew, the messy middle of making something real.',
      details: ['Production diaries', 'Rehearsal footage', 'Cast and crew interviews', 'The making-of, not just the result']
    },
    {
      icon: Headphones,
      title: 'Podcasts & Conversations',
      description: 'Long-form conversations with the people building StrymHub and the wider UK independent scene — actors, writers, directors, students, and collaborators. The platform is the camera; the podcast is the room.',
      details: ['Cast and crew conversations', 'UK independent scene', 'Founder notes and studio diaries', 'Audio and video formats']
    },
    {
      icon: Shield,
      title: 'Built To Last, Not To Trend',
      description: 'StrymHub is a long game. We are not chasing virality, not selling user data, not racing to scale. We are building a studio whose work you will want to revisit in five years, and ten.',
      details: ['Independent and self-funded', 'Quality over quantity', 'No algorithmic feed manipulation', 'Made to be rewatched']
    }
  ];

  const values = [
    {
      name: 'Talent Over Names',
      description: 'We do not chase celebrity. We discover. The actors, writers, and crew you will see on StrymHub were found in community theatres, university campuses, fringe festivals, and open calls. Our job is to give them the screen they have earned but not yet been offered.',
      icon: Users
    },
    {
      name: 'Ownership End-to-End',
      description: 'StrymHub writes, produces, distributes, and streams its own work. We own what we make. That means we can move at our own pace, take creative risks, and share the upside with the people who actually made the work.',
      icon: Shield
    },
    {
      name: 'Local Roots, Global Reach',
      description: 'We are based in Sheffield and we make work that reflects where we are from. But the internet is the cinema now. Our films are made for an audience anywhere in the world who values an honest story told well.',
      icon: Globe
    },
    {
      name: 'The Long Game',
      description: 'A studio is not built in a year. We are building something we expect to be making work under for decades. Consistency, craft, and a back catalogue you can point to — that is the only thing that lasts in this industry.',
      icon: Award
    },
    {
      name: 'Open To Partners & Sponsors',
      description: 'We are an independent studio, but we are not an isolated one. Brand partnerships, sponsored productions, co-funded series, and creative collaborations are a core part of how we plan to grow. We actively welcome conversations with companies, agencies, and creators who want to make something with us.',
      icon: Handshake
    }
  ];

  const timeline = [
    { year: '2023', event: 'StrymHub Founded', description: 'Started as an idea in Sheffield: build a studio that owns its work end-to-end, and a streaming home for it.' },
    { year: '2024', event: 'Platform Built', description: 'The streaming platform was designed and engineered from scratch as the distribution home for everything we produce.' },
    { year: '2025', event: 'First Releases', description: 'Earliest original productions go live. The studio model is tested in public.' },
    { year: '2026', event: 'Building The Repertory', description: 'Partnering with local theatre companies, scouting from universities, and growing the in-house cast and crew.' },
    { year: 'Next', event: 'A Real Catalogue', description: 'A full slate of original films, series, and shows under the StrymHub label \u2014 released on a regular cadence.' },
  ];

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-neutral-900 to-slate-950 text-white">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iYSIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVHJhbnNmb3JtPSJyb3RhdGUoNDUpIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiMwZjE3MmEiLz48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIxLjUiIGZpbGw9IiMzNzMzOGUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjYSkiLz48L3N2Zz4=')] opacity-10"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div>
                <div className="flex items-center space-x-2 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center">
                    <Clapperboard className="h-6 w-6 text-white" />
                  </div>
                  <span className="text-cyan-400 font-semibold text-lg">About StrymHub</span>
                </div>
                
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  An independent studio.
                  <span className="block text-transparent font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text">
                    Streamed where it&apos;s made.
                  </span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-6 leading-relaxed">
                  StrymHub is two things at once: a production company that writes, casts, and films its own
                  original work, and the streaming home where you can watch it. Based in Sheffield, UK —
                  and openly partnering with brands, sponsors, and collaborators along the way.
                </p>
                
                <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                  We scout actors, writers, and crew from local communities, theatre groups, and universities — and
                  build them into an in-house repertory that returns, production after production. The result is
                  original films, series, and shows you will not find anywhere else, made by people building careers
                  in front of you.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/originals"
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-700 hover:to-red-700 transition-all duration-200 hover:scale-105 group"
                  >
                    Watch Our Work
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center px-8 py-4 border border-white/20 text-white font-semibold rounded-xl hover:bg-white/10 transition-all duration-200"
                  >
                    Join The Crew
                  </Link>
                </div>
              </div>
              
              {/* Right Image */}
              <div className="relative">
                <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80"
                    alt="Creative team collaborating on video production at StrymHub"
                    className="w-full h-96 lg:h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-orange-900/20 to-transparent"></div>
                </div>
                
                {/* Platform Status Badge */}
                <div className="absolute top-4 right-4 bg-orange-600/90 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                  <Film className="h-4 w-4 inline mr-1" /> Studio &amp; Streaming Home
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-4 -left-4 w-24 h-24 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-gradient-to-r from-orange-500 to-red-500 rounded-full opacity-20 animate-pulse animation-delay-1000"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Studio Status Banner */}
        <section className="py-8 bg-gradient-to-r from-orange-600 to-red-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center">
                <Info className="h-6 w-6 mr-3" />
                <div>
                  <h3 className="font-semibold">Independent &amp; In-House</h3>
                  <p className="text-sm text-white/80">Every production on StrymHub is written, cast, and made by us</p>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center px-6 py-2 bg-white/20 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/30 transition-all"
              >
                Work With Us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Who We Are, Right Now</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We are early, independent, and honest about it. Here is exactly where StrymHub stands today.
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-200">
                    <stat.icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-800 font-semibold mb-1">
                    {stat.label}
                    {stat.suffix && <span className="block text-sm font-normal">{stat.suffix}</span>}
                  </div>
                  <p className="text-sm text-gray-500">{stat.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-20 bg-gradient-to-r from-slate-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <p>
                    StrymHub started in Sheffield with a simple frustration: there are extraordinary actors,
                    writers, and crew across the UK who never get a real shot. They train, they perform in fringe
                    theatres, they graduate from drama courses, and then most of them spend years waiting for
                    permission from agents and casting directors who are looking for names, not talent.
                  </p>
                  <p>
                    We decided to stop waiting. StrymHub is a production company that scouts its own performers
                    from local communities, theatre groups, and universities, and builds them into a working
                    in-house repertory. The same faces return across different productions. Careers get built in
                    public. The work has continuity — because the people making it do.
                  </p>
                  <p>
                    Everything you watch here is something we wrote, cast, filmed, and edited under our own label.
                    There are no licensed films, no third-party uploads, no algorithmic feed. Just our original
                    work, released on the streaming home we built for it.
                  </p>
                  <p>
                    We are deliberately small, deliberately independent, and deliberately patient. A studio is a
                    long game. We would rather release one production a month that we are proud of than ten that
                    we are not. If that sounds like the kind of thing you want to watch — or be part of — you are
                    in the right place.
                  </p>
                </div>
              </div>
              <div className="space-y-6">
                {timeline.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0 w-20 text-right">
                      <span className="text-orange-600 font-bold">{item.year}</span>
                    </div>
                    <div className="flex-shrink-0 w-px bg-orange-300 relative">
                      <div className="absolute top-1 -left-1.5 w-3 h-3 bg-orange-500 rounded-full"></div>
                    </div>
                    <div className="pb-8">
                      <h4 className="font-bold text-gray-900">{item.event}</h4>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">Our Core Values</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                These principles guide every decision we make, from platform features to creator partnerships.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {values.map((item, index) => (
                <div key={index} className="bg-gradient-to-br from-slate-50 to-orange-50 rounded-2xl p-8 hover:shadow-lg transition-shadow duration-200">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{item.name}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-br from-slate-950 via-neutral-900 to-slate-950 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">What StrymHub Makes</h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                A studio and a streaming home, designed around one idea: that the best independent work deserves to
                be made, seen, and owned by the people who make it.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/10 transition-all duration-200 border border-white/10">
                  <div className="w-14 h-14 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                    <feature.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-4">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-400">
                        <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full mr-2"></span>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Join The Crew Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-orange-600 font-semibold">Join The Crew</span>
                  <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2 py-1 rounded-full">Open Calls</span>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  We are casting. We are crewing. We are listening.
                </h2>
                <div className="space-y-4 text-gray-600 leading-relaxed">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <Info className="h-5 w-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-orange-800 text-sm">
                        <strong>In-house, not user-upload.</strong> StrymHub is not a public upload platform.
                        Everything we stream is produced by us — which means joining StrymHub means joining the
                        company that makes the work.
                      </p>
                    </div>
                  </div>
                  <p>
                    Whether you are an actor, writer, director, DP, editor, sound designer, composer, or theatre
                    group looking for a screen partner — we want to hear from you. Especially if you are based in
                    Sheffield, Yorkshire, the North, or anywhere in the UK.
                  </p>
                  <p>
                    We are particularly interested in long-term collaborators — people who want to be part of a
                    growing repertory and a studio with staying power, not a one-off gig.
                  </p>
                </div>
                <h3 className="font-semibold text-gray-900 mt-6 mb-3">What working with us looks like:</h3>
                <ul className="space-y-3">
                  {[
                    'On-screen and on-set credits on every production',
                    'Profit and revenue share on the work you contribute to',
                    'Recurring roles as the repertory grows',
                    'Your own profile and showreel on StrymHub',
                    'Partnerships with theatre groups and universities welcome',
                    'A long-term creative home, not a one-off booking'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center text-gray-700">
                      <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                        <Star className="w-3 h-3 text-orange-600" />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  className="inline-flex items-center mt-8 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-700 hover:to-red-700 transition-all duration-200 group"
                >
                  Get In Touch
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80"
                  alt="Cast and crew on a StrymHub production set"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute top-4 right-4 bg-orange-600/90 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
                  <Mic className="h-4 w-4 inline mr-1" /> Open To Collaborators
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Partnerships & Sponsorships Section */}
        <section className="py-20 bg-gradient-to-br from-slate-50 to-orange-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                <Handshake className="h-4 w-4" />
                Open For Partnerships &amp; Sponsorships
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Make something with us.
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                StrymHub is an independent studio, but we are not closed off. We actively welcome brand
                partnerships, sponsored productions, co-funded series, and collaborations with other companies
                and creators. If you have a story to tell, a product to launch, or a cause to back — there is a
                version of working together that fits.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {[
                {
                  icon: Megaphone,
                  title: 'Brand Partnerships',
                  description: 'Sponsor an original production, a series, a podcast, or a live event. Get your brand woven into work that audiences actually want to watch — not skipped past.',
                  examples: ['Title sponsorships', 'Episode or series sponsors', 'Pre-roll and in-show placements', 'Co-branded short films']
                },
                {
                  icon: Briefcase,
                  title: 'Sponsored & Branded Content',
                  description: 'Have a story your brand wants told? We can write, cast, shoot, and produce it for you under the StrymHub label — and distribute it to our audience and yours.',
                  examples: ['Branded short films and series', 'Documentary commissions', 'Promotional features', 'Cross-promoted on the platform']
                },
                {
                  icon: Handshake,
                  title: 'Co-Productions & Collaborations',
                  description: 'Filmmakers, theatre groups, production companies, universities, charities, and other studios — we are open to co-producing original work and sharing the credits and the upside.',
                  examples: ['Joint productions', 'Theatre-to-screen adaptations', 'Student and university projects', 'Charity-led storytelling']
                }
              ].map((opp, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-8 shadow-sm border border-orange-100 hover:shadow-lg transition-shadow duration-200">
                  <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-5">
                    <opp.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{opp.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{opp.description}</p>
                  <ul className="space-y-2">
                    {opp.examples.map((ex, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-700">
                        <CheckCircle2 className="h-4 w-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                        {ex}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl p-8 lg:p-10 border border-orange-100 shadow-sm">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                <div className="lg:col-span-2">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    We will say yes more than we say no.
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Whether you are a global brand, a local Sheffield business, a fellow creator, a theatre
                    company, a university department, or a charity — if there is an interesting story or
                    project in it, we want the conversation. Tell us what you have in mind. Budgets, scales,
                    and formats are all flexible.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold rounded-xl hover:from-orange-700 hover:to-red-700 transition-all duration-200 group"
                  >
                    Start A Partnership
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center px-6 py-3 border border-orange-300 text-orange-700 font-semibold rounded-xl hover:bg-orange-50 transition-all duration-200"
                  >
                    Sponsor A Production
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Watch what we make. Or help us make it.
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              StrymHub is an independent studio building for the long term &mdash; and we are openly looking for
              collaborators, sponsors, brand partners, and cast and crew. Sign up to follow new releases, or
              reach out if you want to make something together.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center px-8 py-4 bg-white text-orange-600 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 hover:scale-105 group"
              >
                Create A Free Account
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-xl hover:bg-white/10 transition-all duration-200"
              >
                Partner With Us
              </Link>
            </div>
          </div>
        </section>
      </div>
    </MainLayout>
  );
};

export default AboutPage;